import { useState } from 'react';
import { useCustom, useCustomMutation, useApiUrl } from '@refinedev/core';
import { ClipboardCheck, CheckCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FormHeader } from '@/components/forms';
import {
  DataTable,
  TableToolbar,
  type Column,
  type SortState,
  type BulkAction,
} from '@/components/tables';
import { HomeworkReviewDialog } from './homework-review-dialog';
import { useConfirm } from '@/hooks/use-confirm';
import { HomeworkStatus } from '@masters-club/types';
import {
  type HomeworkRecord,
  getUserDisplayName,
  HOMEWORK_STATUS_LABELS,
  HOMEWORK_STATUS_VARIANTS,
} from './types';
import { notify } from '@/lib/notify';

// ─── Types ──────────────────────────────────────────────────

interface HomeworkListResponse {
  data: HomeworkRecord[];
  meta: {
    page: number;
    perPage: number;
    total: number;
    totalPages: number;
  };
}

interface BulkReviewResponse {
  processed: number;
  skipped: number;
}

// ─── Constants ──────────────────────────────────────────────

const PAGE_SIZE = 20;

const STATUS_FILTER_ALL = 'all';

const dateFormatter = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const formatDate = (value: string | null | undefined): string => {
  if (!value) { return '—'; }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : dateFormatter.format(date);
};

// ─── Component ──────────────────────────────────────────────

export function HomeworkList() {
  const apiUrl = useApiUrl();
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState | undefined>({ field: 'submittedAt', order: 'desc' });
  const [statusFilter, setStatusFilter] = useState<string>(STATUS_FILTER_ALL);
  const [userSearch, setUserSearch] = useState('');
  const [reviewDialog, setReviewDialog] = useState<HomeworkRecord | null>(null);

  const { confirm, ConfirmationDialog } = useConfirm();

  // Собираем query params
  const queryParams: Record<string, string> = {
    page: String(page),
    limit: String(PAGE_SIZE),
  };

  if (statusFilter !== STATUS_FILTER_ALL) {
    queryParams.status = statusFilter;
  }

  if (userSearch.trim()) {
    queryParams.userId = userSearch.trim();
  }

  const { query: homeworkQuery } = useCustom<HomeworkListResponse>({
    url: `${apiUrl}/homework`,
    method: 'get',
    config: {
      query: queryParams,
    },
  });
  const response = homeworkQuery.data;
  const isLoading = homeworkQuery.isLoading;
  const refetch = homeworkQuery.refetch.bind(homeworkQuery);

  const { mutate: bulkReview } = useCustomMutation<BulkReviewResponse>();

  const homeworkData = response?.data ?? null;
  const items: HomeworkRecord[] = homeworkData?.data ?? [];
  const total = homeworkData?.meta?.total ?? 0;

  // ─── Sort handler (client-side по текущей странице) ───────

  const handleSortChange = (newSort: SortState | undefined) => {
    setSort(newSort);
  };

  const sortedItems = (() => {
    if (!sort) { return items; }
    return [...items].sort((a, b) => {
      const field = sort.field as keyof HomeworkRecord;
      const aVal = a[field];
      const bVal = b[field];
      if (aVal === null || aVal === undefined) { return 1; }
      if (bVal === null || bVal === undefined) { return -1; }
      const cmp = String(aVal) < String(bVal) ? -1 : String(aVal) > String(bVal) ? 1 : 0;
      return sort.order === 'asc' ? cmp : -cmp;
    });
  })();

  // ─── Bulk actions ─────────────────────────────────────────

  const handleBulkApprove = (selected: HomeworkRecord[]) => {
    const submittedIds = selected
      .filter((hw) => hw.status === HomeworkStatus.SUBMITTED)
      .map((hw) => hw.id);

    if (submittedIds.length === 0) {
      notify.warning('Нет отправленных ДЗ среди выбранных для одобрения');
      return;
    }

    void confirm({
      title: 'Одобрить домашние задания',
      description: `Будет одобрено ${submittedIds.length} из ${selected.length} выбранных ДЗ (только со статусом «Отправлено»).`,
      confirmText: 'Одобрить',
    }).then((confirmed) => {
      if (!confirmed) { return; }
      bulkReview(
        {
          url: `${apiUrl}/homework/bulk-review`,
          method: 'post',
          values: { ids: submittedIds, status: HomeworkStatus.REVIEWED },
        },
        {
          onSuccess: (res) => {
            const { processed, skipped } = res.data;
            notify.success(`Одобрено: ${processed}. Пропущено: ${skipped}`);
            void refetch();
          },
          onError: () => {
            notify.error('Ошибка при массовом одобрении');
          },
        },
      );
    });
  };

  const bulkActions: BulkAction<HomeworkRecord>[] = [
    {
      label: 'Одобрить',
      icon: <CheckCircle className='h-4 w-4' />,
      variant: 'default',
      showOnlyWhenSelected: true,
      onClick: handleBulkApprove,
    },
  ];

  // ─── Columns ──────────────────────────────────────────────

  const columns: Column<HomeworkRecord>[] = [
    {
      key: 'user',
      header: 'Ученик',
      render: (_, row) => (
        <div className='flex flex-col'>
          <span className='font-medium text-sm'>{getUserDisplayName(row.user)}</span>
          {row.user.telegramUsername && (
            <span className='text-xs text-muted-foreground'>@{row.user.telegramUsername}</span>
          )}
        </div>
      ),
    },
    {
      key: 'item',
      header: 'Задание',
      hiddenOnMobile: true,
      render: (_, row) => (
        <div className='flex flex-col max-w-[300px]'>
          <span className='text-sm truncate'>{row.item.title}</span>
          <span className='text-xs text-muted-foreground truncate'>
            {row.item.module.course.title} &rarr; {row.item.module.title}
          </span>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Статус',
      width: '130px',
      sortable: true,
      render: (_, row) => (
        <Badge variant={HOMEWORK_STATUS_VARIANTS[row.status]}>
          {HOMEWORK_STATUS_LABELS[row.status]}
        </Badge>
      ),
    },
    {
      key: 'grade',
      header: 'Оценка',
      width: '90px',
      hiddenOnMobile: true,
      sortable: true,
      render: (_, row) => (
        <span className='text-sm'>
          {row.grade !== null ? (
            <span className='font-medium'>{row.grade}<span className='text-muted-foreground'> / 100</span></span>
          ) : (
            <span className='text-muted-foreground'>&mdash;</span>
          )}
        </span>
      ),
    },
    {
      key: 'submittedAt',
      header: 'Отправлено',
      width: '160px',
      hiddenOnMobile: true,
      sortable: true,
      render: (_, row) => (
        <span className='text-sm text-muted-foreground'>
          {formatDate(row.submittedAt)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '120px',
      render: (_, row) => (
        <Button
          variant='outline'
          size='sm'
          className='text-xs'
          onClick={(e) => {
            e.stopPropagation();
            setReviewDialog(row);
          }}
        >
          <ClipboardCheck className='h-3.5 w-3.5 mr-1' />
          Рецензия
        </Button>
      ),
    },
  ];

  // ─── Render ──────────────────────────────────────────────

  return (
    <div className='space-y-6'>
      <FormHeader
        title='Домашние задания'
        sticky
      />

      <div className='flex flex-wrap items-center gap-4'>
        <TableToolbar
          searchValue={userSearch}
          onSearchChange={(v) => { setUserSearch(v); setPage(1); }}
          searchPlaceholder='ID пользователя...'
        />
        <Select
          value={statusFilter}
          onValueChange={(v) => { setStatusFilter(v); setPage(1); }}
        >
          <SelectTrigger className='w-[170px]'>
            <SelectValue placeholder='Все статусы' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={STATUS_FILTER_ALL}>Все статусы</SelectItem>
            <SelectItem value={HomeworkStatus.NOT_STARTED}>{HOMEWORK_STATUS_LABELS[HomeworkStatus.NOT_STARTED]}</SelectItem>
            <SelectItem value={HomeworkStatus.SUBMITTED}>{HOMEWORK_STATUS_LABELS[HomeworkStatus.SUBMITTED]}</SelectItem>
            <SelectItem value={HomeworkStatus.REVIEWED}>{HOMEWORK_STATUS_LABELS[HomeworkStatus.REVIEWED]}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable
        columns={columns}
        data={sortedItems}
        total={total}
        page={page}
        pageSize={PAGE_SIZE}
        isLoading={isLoading}
        onPageChange={setPage}
        onRowClick={(row) => setReviewDialog(row)}
        emptyMessage='Нет домашних заданий'
        rowKey='id'
        sort={sort}
        onSortChange={handleSortChange}
        bulkActions={bulkActions}
      />

      <HomeworkReviewDialog
        homework={reviewDialog}
        open={reviewDialog !== null}
        onOpenChange={(v) => { if (!v) { setReviewDialog(null); } }}
        onReviewed={() => { void refetch(); }}
      />

      {ConfirmationDialog}
    </div>
  );
}
